import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import "client-application"; // eslint-disable-line import/no-unresolved
import { ProductCategory } from "@rtr/godmother";
import { CacheDurations, withCdnAndBrowserCaching } from "helpers/cache-control-helpers";
import Instrumentation from "helpers/instrumentation";
import {
  ReviewsSort,
  defaultFitCounts,
  flagsAndExperimentNames,
  merchandiseCategories,
  pageTypes,
} from "rtr-constants";
import productsActions from "actions/products-actions";
import PhotoReviewsServiceClient from "clients/PhotoReviewsServiceClient";
import ProductsServiceClient from "clients/ProductsServiceClient";
import { godmotherModelAsPojo, listSerializationHelper, fetchProduct } from "helpers/next-helpers";
import { capitalize } from "helpers/FormattingUtils";
import { idPropType, productPropType, productCategoryPropType } from "components/propTypes";
import { useUser } from "components/layout/UserContext";
import { useMembershipState } from "components/layout/MembershipStateContext";
import { useBrowser } from "components/layout/BrowserContext";
import { ReviewsProvider, useReviews } from "components/layout/ReviewsContext";
import PDP from "components/source/products/pdp";
import UniversalPDP from "components/source/universal_pdp/pdp";
import { SFLogger } from "logger/logger";
import { getPriceDisplay } from "helpers/pricing-helper";
import { getFlagValue } from "helpers/server-side-only/flag-helpers";
import CmsServiceClient, { Locations } from "clients/CmsServiceClient";
import { DiscoSearchServiceClient } from "clients/disco-search-service-client";
import { fetchReviewsWithCoverPhoto } from "../../../../api/products/[productId]/cover-photos";
import {
  getOffsetFromThumbnailPage,
  THUMBNAIL_PAGE_SIZE,
} from "components/source/universal_pdp/review-thumbnail/review-thumbnail-carousel";

const logger = SFLogger("PDP");

function ProductDetailPage(props) {
  const {
    acquisitionMessaging,
    product,
    productCategory,
    productCategories,
    productId,
    reviews: providedReviews,
  } = props;

  if (props.version === "universal_pdp") {
    return <UniversalPDP />;
  }
  const dispatch = useDispatch();
  const { user } = useUser();
  const { membershipState } = useMembershipState();
  const browser = useBrowser();
  const { reviews, hasLoaded: reviewsHaveLoaded, setProductId: setProductIdForReviews } = useReviews();

  useEffect(() => {
    dispatch(productsActions.setCurrentProduct(product));
    setProductIdForReviews(productId);
    dispatch(productsActions.setMembershipLensOnPageLoad(user, product));
  }, []);

  // Once membershipState is available, set a default lens if necessary.
  // i.e. subscribers should default to a subscriber lens
  useEffect(() => {
    dispatch(productsActions.setDefaultMembershipLensForSubscriber(membershipState));
  }, [membershipState?.inventoryEligibilities?.length]);

  return (
    <PDP
      acquisitionMessaging={acquisitionMessaging}
      browser={browser}
      dispatch={dispatch}
      product={product}
      productCategory={productCategory}
      productCategories={productCategories}
      reviews={(reviewsHaveLoaded && reviews) || providedReviews || {}}
      shouldLoadReviews={false}
      userData={user}
      membershipState={membershipState}
    />
  );
}

ProductDetailPage.propTypes = {
  // acquisitionMessagingProvider
  acquisitionMessaging: PropTypes.shape({
    unlimited: PropTypes.object,
    unlimitedWithSku: PropTypes.object,
    update: PropTypes.object,
    updateWithSku: PropTypes.object,
  }),
  product: productPropType,
  productCategory: productCategoryPropType,
  productCategories: PropTypes.arrayOf(productCategoryPropType),
  productId: idPropType,

  // while reviews should be lazy-loaded, we mash reviews and reviewsSummary
  // into a single prop, so for server-side rendering, the summary will be
  // populated, but individual reviews will not (for SEO and UX reasons)
  // TODO: break this up! the result is a bunch of extra calls to PhotoReviews
  // where the summary data does not change
  reviews: PropTypes.shape({
    averageRating: PropTypes.number,
    count: PropTypes.number,
    currentCount: PropTypes.number,
  }),
  version: PropTypes.string,
};

const pageType = pageTypes.PDP;
const pageName = pageTypes.PDP;

ProductDetailPage.getProviders = () => [ReviewsProvider];
ProductDetailPage.pageType = pageType; // for logging/legacy
ProductDetailPage.pageName = pageName; // for logging/legacy

export default ProductDetailPage;

///////////////////////////////
//                           //
//  Data-Fetching Functions  //
//                           //
///////////////////////////////

// TODO: merge this with the logic in the reviews API
const defaultSummary = {
  fitCounts: defaultFitCounts,
};
async function fetchReviewSummary(productId) {
  try {
    const summary = await PhotoReviewsServiceClient.getInstance().fetchReviewSummary(productId);

    let fitRating = null;
    if (summary?.fitCounts) {
      const fitRatingTotal = Object.values(summary.fitCounts).reduce((a, b) => a + b, 0);
      if (fitRatingTotal > 0) {
        fitRating = summary.fitCounts.Large / fitRatingTotal - summary.fitCounts.Small / fitRatingTotal;
      }
    }

    // gross logic from Ruby
    // the summary and the reviews data are mashed into one response. for server
    // rendering, we only want the summary (data, the reviews, is set to null)
    return {
      averageRating: summary?.averageStarRating || null,
      count: summary?.reviewCount,
      currentCount: summary?.reviewCount,
      data: null,
      fitRating,
      item: { id: productId },
      loading: false,
      page: 1,
      sort: ReviewsSort.FEATURED,
      summary: summary || defaultSummary,
    };
  } catch (error) {
    logger.error(`Failed to fetch reviews for: '${productId}'`, { error });
    return null;
  }
}

async function fetchProductFromSlug(designerSlug, productSlug) {
  try {
    return await ProductsServiceClient.getInstance().fetchProductFromSlug(designerSlug, productSlug);
  } catch (error) {
    logger.error(`Failed to fetch Product from path: '${designerSlug}/${productSlug}'`, { error });
    return null;
  }
}

export async function fetchProductCategory(categoryId) {
  if (!categoryId) return null;

  try {
    return await ProductCategory.find(categoryId);
  } catch (e) {
    logger.error(`Failed to fetch ProductCategory (id=${categoryId}): ${e.message}`);
    return null;
  }
}

export async function fetchProductCategories() {
  try {
    return await ProductCategory.list({
      fields: {
        productCategory: "parent",
      },
    });
  } catch (e) {
    logger.error(`Failed to fetch ProductCategories ${e?.message}`);
    return null;
  }
}

async function fetchMembershipPricing() {
  try {
    return CmsServiceClient.getInstance().getContent("/content/membership_pricing", Locations.storefrontJson);
  } catch (e) {
    logger.error(`Failed to fetch Membership Pricing ${e?.message}`);
    return null;
  }
}

export const getServerSideProps = async context => {
  const {
    params: { designerSlug, productSlug },
    req,
    req: { url },
    res,
    query,
  } = context;

  if (query?.version === "universal_pdp") {
    Instrumentation.tagPage("universal_pdp");
    return await getUniversalPdpServerSideProps(context);
  }

  Instrumentation.tagPage("pdp");

  if (query.retail && query.retail === "true") {
    // Redirect Google Shopping traffic to use the correct param, `buynow=true`
    return {
      redirect: {
        source: url,
        destination: url.replace("retail=true", "buynow=true"),
        permanent: true,
      },
    };
  }

  withCdnAndBrowserCaching(res, req, { "s-maxage": CacheDurations.LONG });
  logger.debug(`start getServerSideProps with ${designerSlug} and ${productSlug} `);
  const legacyProduct = await fetchProductFromSlug(designerSlug, productSlug);
  const { styleName: productId } = legacyProduct || {};

  if (!productId) {
    logger.warn(`Product Id not found (Product Catalog) designer=${designerSlug} product=${productSlug}`);

    return {
      notFound: true,
    };
  }

  // parallelize the data requests for this page
  const [
    membershipPricingResponse,
    productResponse,
    reviewSummaryResponse,
    staticProductResponse,
    reviewsCoverPhotosResponse,
    reviewsThumbnailResponse,
    notifyMeFlagResponse,
  ] = await Promise.allSettled([
    fetchMembershipPricing(),
    fetchProduct(productId),
    fetchReviewSummary(productId),
    // adding Single Products API with URL call because once we remove godmother fetch we wont have
    // product ID in the PDP during SSR
    DiscoSearchServiceClient.getInstance().getProductsDataByPdpUrl(url?.split("?")[0]),
    fetchReviewsWithCoverPhoto({ isFeatured: true }, productId),
    fetchReviewsWithCoverPhoto(
      {
        offset: getOffsetFromThumbnailPage(1),
        limit: THUMBNAIL_PAGE_SIZE,
        orderBy: "featured",
      },
      productId
    ),
    getFlagValue(flagsAndExperimentNames.SF_SACT_250_NOTIFY_ME, req, res),
  ]);

  const product = productResponse.value;
  const notFound = productResponse.status === "rejected" || !product;
  const membershipPricing = membershipPricingResponse.value;

  if (notFound) {
    logger.warn(`PDP Not Found product (Godmother) designer=${designerSlug} product=${productSlug}`);
  }

  // unfortunately, this one cannot be parallelized
  const productCategory = await fetchProductCategory(product?.category?.id);
  const productCategories = await fetchProductCategories();

  // TODO: Grab all this data from the BE
  const props = {
    initialReduxState: {
      // Proxy to ruby layer when submitting form
      action: `/api/storefront${url}`,
      basePath: (url || "").split("?")[0],
      isBuyNow: query.buynow === "true",
      membershipPricing: membershipPricing?.content,
      userData: {
        isClassicLens: query.lens === "classic",
        isUnlimitedLens: query.lens === "unlimited",
        isRTRUpdateLens: query.lens === "rtrUpdate",
        lens: query.lens || null,
      },
      product: { id: productId },
      staticProduct: staticProductResponse.value?.items?.[0] || {},
      reviewsCoverPhotos: reviewsCoverPhotosResponse?.value?.moments || [],
      reviewsThumbnailPhotos: reviewsThumbnailResponse?.value || [],
      // adding this in redux so that we dont prop drill the value to every component
      // Planning to use this on the newer components of the new PDP, so that we can remove
      // the prop drilling in the universal PDP
      reviews: reviewSummaryResponse?.value || {},
      flagsAndExperiments: {
        [flagsAndExperimentNames.SF_SACT_250_NOTIFY_ME]: notifyMeFlagResponse?.value,
      },
    },
    productId,
    // Next doesn't like instances of Godmother Classes
    // got to do some extra work here to handle prices from Pricing service
    // until GM returns product.price
    product: godmotherModelAsPojo(product),
    productCategory: godmotherModelAsPojo(productCategory),
    productCategories: listSerializationHelper(productCategories),
    reviews: reviewSummaryResponse?.value || {},
    // Legacy values still used today, not provided on the modern equivalent
    // mostly used for page <meta /> tags
    // cherry picked to give a degree of traceability
    legacyProduct: {
      legacyProductURL: legacyProduct.legacyProductURL,
      imgOriginalFrontURL: legacyProduct.imgOriginalFrontURL,
      imgThumbnailFrontURL: legacyProduct.imgThumbnailFrontURL,
      formality: legacyProduct.formality,
      sizeScaleName: legacyProduct.sizeScaleName,
      type: legacyProduct.type,
      designer: {
        searchableName: legacyProduct.designer.searchableName,
      },
    },
  }; // will be passed to the page component as props

  return {
    props,
    notFound,
    // revalidate: 60, // regenerate pages after 60 seconds?
  };
};

const PAGE_SIZE = 40;

ProductDetailPage.getHeadData = (pageProps, url) => {
  if (pageProps?.version === "universal_pdp") {
    return getUniversalPdpHeadData(pageProps);
  }
  const { product: item, reviews, legacyProduct } = pageProps;

  let titleAddition = `${item?.displayName} by ${item?.designer?.displayName}`;

  const priceDisplay = getPriceDisplay(item?.price);

  if (priceDisplay) titleAddition += ` for ${priceDisplay}`;

  let description = /* item.clearance? || item.saleableproduct?
    ? `Buy ${titleAddition} from Rent the Runway.`
    :*/ `Rent ${titleAddition} only at Rent the Runway.`;

  // is this the right places for reviews? i.e., does this code execute
  // dynamically if the page is cached?
  if (reviews?.page > 1) {
    description += ` - Page ${reviews?.page}`;
  }

  return {
    titleAddition,
    branchDeeplinkUrl: `rtr://product/${item?.id}?presentationStyle=Modal&navVisible=1`,
    canonical: legacyProduct.legacyProductURL,
    metadata: {
      description,
      pageName,
      pageType,
      "og:title": `${item?.designer?.displayName} ${item?.displayName}`,
      "og:description": description,
      "og:url": legacyProduct.legacyProductURL,
      "og:image": legacyProduct.imgOriginalFrontURL,
    },
    extraHead: () => {
      return (
        <>
          {reviews?.page > 1 && (
            <link href={`${url.replace(/\/review-\S*/, "")}/review-p${reviews?.page - 1}`} rel="prev"></link>
          )}
          {reviews?.page <= Math.ceil(reviews?.count / PAGE_SIZE) && (
            <link href={`${url.replace(/\/review-\S*/, "")}/review-p${reviews?.page + 1}`} rel="next"></link>
          )}
        </>
      );
    },
    sailthru: () => {
      const tags = getSailthruTagsFromItem(item, legacyProduct);

      return (
        <>
          {/* <meta name="sailthru.available.from" content="" /> */}
          {/* <meta name="sailthru.available.to" content="" /> */}
          <meta
            name="sailthru.clearance"
            content={(item?.price?.merchandiseCategory === merchandiseCategories.clearance).toString()}
          />
          <meta name="sailthru.colors" content={(item?.colors || []).map(c => capitalize(c)).join(",")} />
          {/* <meta name="sailthru.date" content="" /> */}
          <meta name="sailthru.designer" content={item?.designer?.displayName} />
          <meta name="sailthru.formality" content={legacyProduct.formality} />
          <meta name="sailthru.image.full" content={legacyProduct.imgOriginalFrontURL} />
          <meta name="sailthru.image.thumb" content={legacyProduct.imgThumbnailFrontURL} />
          {/* <meta name="sailthru.price.rental" content="" /> */}
          {/* <meta name="sailthru.price.retail" content={item.retail_price} /> */}
          <meta name="sailthru.size.scale" content={legacyProduct.sizeScaleName} />
          <meta name="sailthru.tags" content={tags.join(", ").trim()} />
          <meta name="sailthru.title" content={item?.displayName} />
          <meta name="sailthru.type" content={legacyProduct.type} />
        </>
      );
    },
  };
};

const getSailthruTagsFromItem = (item, legacyProduct) => {
  const tagsArr = [];
  if (item?.clearance) {
    tagsArr.push(`clearance-${item?.clearance}`);
  }
  if (Array.isArray(item?.colors)) {
    tagsArr.push(`color-${item?.colors.join("")}`);
  }
  if (legacyProduct.designer?.searchableName) {
    tagsArr.push(`designer-${legacyProduct.designer.searchableName}`);
  }
  if (Array.isArray(legacyProduct.formality)) {
    tagsArr.push(`formality-${legacyProduct.formality}`);
  }
  if (legacyProduct.sizeScaleName) {
    tagsArr.push(`sizescale-${legacyProduct.sizeScaleName}`);
  }
  if (legacyProduct.type) {
    tagsArr.push(`type-${legacyProduct.type}`);
  }
  return tagsArr.map(t => t.toLowerCase());
};

ProductDetailPage.getCssChunkNames = () => ["application-pdp"];
/*
 *
 *    The below functions belong to the new PDP we are redesigning
 *    present under the folder sources/universal_pdp
 *
 */

export const getUniversalPdpServerSideProps = async ctx => {
  const {
    req: { url },
  } = ctx;

  const staticProductResponse = await DiscoSearchServiceClient.getInstance().getProductsDataByPdpUrl(
    url?.split("?")[0]
  );

  const staticProduct = staticProductResponse?.items?.[0] || {};

  const [reviewSummaryResponse, reviewsCoverPhotosResponse, reviewsThumbnailResponse] = await Promise.allSettled([
    fetchReviewSummary(staticProduct.productId),
    fetchReviewsWithCoverPhoto({}, staticProduct.productId),
    fetchReviewsWithCoverPhoto(
      {
        offset: getOffsetFromThumbnailPage(1),
        limit: THUMBNAIL_PAGE_SIZE,
        orderBy: "featured",
      },
      staticProduct.productId
    ),
  ]);

  return {
    props: {
      initialReduxState: {
        staticProduct,
        reviewsCoverPhotos: reviewsCoverPhotosResponse?.value?.moments || [],
        reviewsThumbnailPhotos: reviewsThumbnailResponse?.value || [],
        reviews: reviewSummaryResponse?.value || {},
      },
      version: "universal_pdp",
    },
  };
};

export const getUniversalPdpHeadData = pageProps => {
  const { staticProduct: product } = pageProps.initialReduxState;

  const titleAddition = `${product?.displayName} by ${product?.designerDisplayName}`;
  const description = /* item.clearance? || item.saleableproduct?
    ? `Buy ${titleAddition} from Rent the Runway.`
    :*/ `Rent ${titleAddition} only at Rent the Runway.`;
  const canonical = product.pdpUrl;

  return {
    titleAddition,
    description,
    canonical,
  };
};
